import { ArrowDown, ArrowUp } from "@reverb-ui/icons-react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Box,
  Tooltip,
} from "@reverb-ui/react";
import { useTable, useSortBy } from "react-table";
import { useEffect, useMemo, useRef, useState } from "react";

type TSortedTableProps = {
  dataColumns: {
    header: string | JSX.Element;
    accessor: string;
    tooltipMessage?: string;
  }[];
  dataRows: Record<number|string, string | number | JSX.Element>[];
};

export const SortedTable = ({ dataRows, dataColumns }: TSortedTableProps) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns: dataColumns, data: dataRows }, useSortBy);

  const hearderGroupMemo = useMemo(
    () => headerGroups.map((headergroup) => headergroup),
    [headerGroups]
  );
  const tableBodyProps = useMemo(
    () => getTableBodyProps(),
    [getTableBodyProps]
  );
  const tableProps = useMemo(() => getTableProps(), [getTableProps]);
  const rowsMemo = useMemo(() => rows.map((row) => row), [rows]);

  const renderRow = () => {
    return rowsMemo.map((row) => {
      prepareRow(row);
      return (
        <Tr {...row.getRowProps()}>
          {row.cells.map((cell) => (
            <Td
              {...cell.getCellProps()}
              maxW={dataColumns.length <= 5 ? "15rem" : "12rem"}
              _notFirst={{
                textAlign: "center",
              }}
              isTruncated
            >
              <CellRow>{cell.value}</CellRow>
            </Td>
          ))}
        </Tr>
      );
    });
  };

  return (
    <Table h="min-content" variant="simple" {...tableProps}>
      <Thead>
        {hearderGroupMemo.map((headerGroup) => (
          <Tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => (
              <Tooltip 
                key={column.getHeaderProps(column.getSortByToggleProps()).key}
                bg="gray.100" 
                color="gray.800"
                label={dataColumns?.find(_ => _.accessor === column.id)?.tooltipMessage}
                isDisabled={!dataColumns?.find(_ => _.accessor === column.id)?.tooltipMessage}
              >
                <Th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  borderBottomColor="gray.400"
                  onKeyPress={() => column.toggleSortBy()}
                  tabIndex={0}
                  _focus={{
                    outlineColor: "blue.200",
                  }}
                  textTransform="capitalize"
                  fontSize="sm"
                  fontWeight="bold"
                  whiteSpace={"nowrap"}
                  _notFirst={{
                    textAlign: "-webkit-center",
                  }}
                >
                  <Flex width="fit-content">
                    {column.render("header")}
                    {column.isSorted ? (
                      column.isSortedDesc ? (
                        <Box
                          color="blue.500"
                          _hover={{ color: "neutral.500" }}
                          _focus={{ color: "red.500" }}
                        >
                          <ArrowDown
                            color="current"
                            aria-label="sorted descending"
                          />
                        </Box>
                      ) : (
                        <Box color="blue.500" _hover={{ color: "neutral.500" }}>
                          <ArrowUp
                            color="current"
                            aria-label="sorted ascending"
                          />
                        </Box>
                      )
                    ) : (
                      <Box color="neutral.400" _hover={{ color: "neutral.500" }}>
                        <ArrowUp color="current" aria-label="sorted ascending" />
                      </Box>
                    )}
                  </Flex>
                </Th>
              </Tooltip>
            ))}
          </Tr>
        ))}
      </Thead>
      <Tbody {...tableBodyProps}>{renderRow()}</Tbody>
    </Table>
  );
};

const CellRow = ({ children }: { children: React.ReactNode }) => {
  const ref = useRef(null);
  const [isOverflown, setIsOverflown] = useState(false);
  useEffect(() => {
    const element: {
      scrollWidth: number;
      clientWidth: number;
    } = ref.current!;
    setIsOverflown(element.scrollWidth > element.clientWidth);
  }, []);

  return (
    <Tooltip
      label={children}
      isDisabled={!isOverflown}
      bg="gray.100"
      color="gray.800"
    >
      <Box position="relative" isTruncated ref={ref}>
        {children}
      </Box>
    </Tooltip>
  );
};
